import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import {
  UserCheck, Clock, DollarSign, Smartphone,
  Sparkles, MessageSquare, FileText, CreditCard,
  TrendingUp, UserPlus, ChevronRight, Send, AlertTriangle
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar
} from 'recharts';
import { API } from '@/App';

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

const AdminDashboardHome = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [installStats, setInstallStats] = useState(null);
  const [pendingCounts, setPendingCounts] = useState({});
  const [msgStats, setMsgStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [copilotQuery, setCopilotQuery] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const h = { Authorization: `Bearer ${token}` };
      const [userRes, statsRes, analyticsRes, installRes, pendingRes, msgRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers: h }),
        axios.get(`${API}/admin/stats`, { headers: h }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/dashboard-analytics`, { headers: h }).catch(() => ({ data: null })),
        axios.get(`${API}/settings/install-stats`, { headers: h }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/pending-counts`, { headers: h }).catch(() => ({ data: {} })),
        axios.get(`${API}/messaging/stats`, { headers: h }).catch(() => ({ data: {} })),
      ]);
      setUser(userRes.data);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setInstallStats(installRes.data);
      setPendingCounts(pendingRes.data);
      setMsgStats(msgRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    return h < 12 ? 'Bonjour' : h < 18 ? 'Bon après-midi' : 'Bonsoir';
  };

  const handleCopilotSubmit = () => {
    if (!copilotQuery.trim()) return;
    navigate('/admin/copilot', { state: { initialMessage: copilotQuery } });
  };

  const SUGGESTIONS = [
    "Rédige une actualité pour le prochain congrès",
    "Propose un email de relance adhésions",
    "Rédige un texte pour la page Qui sommes-nous",
  ];

  // KPI indicators
  const kpis = [
    { label: 'Adhérents à jour', value: stats?.active_subscriptions || 0, icon: UserCheck, color: '#059669', bg: '#ecfdf5', path: '/admin/members' },
    { label: 'Nouveaux (30j)', value: stats?.new_members_30d || 0, icon: UserPlus, color: '#35AFE4', bg: '#eaf6fc', path: '/admin/members' },
    { label: 'Expirent (30j)', value: stats?.expiring_30d || 0, icon: Clock, color: '#d97706', bg: '#fffbeb', path: '/admin/renewal-reminders' },
    { label: `Cotisations ${new Date().getFullYear()}`, value: `${stats?.total_revenue || 0}€`, icon: DollarSign, color: '#7c3aed', bg: '#f5f3ff', path: '/admin/paiements' },
    { label: 'Installations app', value: installStats?.total || 0, icon: Smartphone, color: '#0891b2', bg: '#ecfeff', path: '/admin/analytics' },
    { label: 'Messages ouverts', value: msgStats?.open || 0, icon: MessageSquare, color: '#dc2626', bg: '#fef2f2', path: '/admin/messaging' },
  ];

  // Action panels
  const actionPanels = [
    {
      title: 'Comptes à valider',
      count: pendingCounts.pending_adhesions || 0,
      icon: UserCheck,
      color: '#7c3aed',
      bg: '#f5f3ff',
      path: '/admin/members',
      desc: 'Membres en attente de validation',
    },
    {
      title: 'Messages à traiter',
      count: msgStats?.unread || 0,
      icon: MessageSquare,
      color: '#0891b2',
      bg: '#ecfeff',
      path: '/admin/messaging',
      desc: 'Demandes non lues des membres',
    },
    {
      title: 'Articles à modérer',
      count: pendingCounts.pending_articles || 0,
      icon: FileText,
      color: '#059669',
      bg: '#ecfdf5',
      path: '/admin/articles',
      desc: 'Publications soumises à valider',
    },
    {
      title: 'Adhésions à renouveler',
      count: stats?.expiring_30d || 0,
      icon: CreditCard,
      color: '#d97706',
      bg: '#fffbeb',
      path: '/admin/renewal-reminders',
      desc: 'Membres expirant sous 30 jours',
    },
  ];

  // Chart data
  const trafficData = analytics?.traffic ? MONTHS.map((m, i) => ({
    name: m,
    [`${analytics.traffic.year}`]: analytics.traffic.current_year?.[i]?.views || 0,
    [`${analytics.traffic.year - 1}`]: analytics.traffic.previous_year?.[i]?.views || 0,
  })) : [];

  const adhesionData = analytics?.adhesions ? MONTHS.map((m, i) => ({
    name: m,
    [`${analytics.adhesions.year}`]: analytics.adhesions.current_year?.[i]?.count || 0,
    [`${analytics.adhesions.year - 1}`]: analytics.adhesions.previous_year?.[i]?.count || 0,
  })) : [];

  const revenueData = analytics?.revenue ? MONTHS.map((m, i) => ({
    name: m,
    [`${analytics.revenue.year}`]: analytics.revenue.current_year?.[i]?.amount || 0,
    [`${analytics.revenue.year - 1}`]: analytics.revenue.previous_year?.[i]?.amount || 0,
  })) : [];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#35AFE4] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 min-h-full bg-[#f8f9fb]" data-testid="admin-dashboard">

        {/* ─── 1. WELCOME BANNER + COPILOT ─────────────────────────────── */}
        <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2a5a8f] rounded-2xl p-5 lg:p-6 mb-5 text-white" data-testid="welcome-banner">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1">
              <h1 className="text-xl lg:text-2xl font-bold mb-1" data-testid="welcome-title">
                {getGreeting()}, {user?.first_name}
              </h1>
              <p className="text-sm text-white/60">Tableau de bord SFOALC</p>
            </div>
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <Sparkles size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  value={copilotQuery}
                  onChange={e => setCopilotQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCopilotSubmit(); }}
                  placeholder="Demandez au copilote..."
                  className="w-full pl-9 pr-10 py-2.5 bg-white/10 border border-white/20 rounded-xl text-sm text-white placeholder:text-white/40 focus:outline-none focus:bg-white/15 focus:border-white/40 transition-all"
                  data-testid="copilot-input-dashboard"
                />
                <button onClick={handleCopilotSubmit}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                  data-testid="copilot-submit-dashboard">
                  <Send size={14} className="text-white/60" />
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => { setCopilotQuery(s); navigate('/admin/copilot', { state: { initialMessage: s } }); }}
                    className="text-[10px] px-2.5 py-1 bg-white/10 border border-white/15 rounded-full text-white/70 hover:bg-white/20 hover:text-white transition-all truncate max-w-[200px]"
                    data-testid={`copilot-suggestion-${i}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ─── CONFIG ALERT ────────────────────────────────────────────── */}
        {(() => {
          const missing = [];
          if (!stats?.total_revenue && !stats?.active_subscriptions) missing.push('HelloAsso (paiements)');
          if (pendingCounts?.pending_adhesions > 5) missing.push(`${pendingCounts.pending_adhesions} comptes en attente`);
          if (stats?.expiring_30d > 10) missing.push(`${stats.expiring_30d} adhésions expirent sous 30j`);
          if (!missing.length) return null;
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5 flex items-start gap-3" data-testid="config-alert">
              <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-amber-900">Points d'attention</p>
                <ul className="mt-1 space-y-0.5">
                  {missing.map((m, i) => <li key={i} className="text-xs text-amber-700">{m}</li>)}
                </ul>
              </div>
            </div>
          );
        })()}

        {/* ─── 2. SIX KPI INDICATORS ───────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5" data-testid="kpi-grid">
          {kpis.map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <button key={i} onClick={() => navigate(kpi.path)}
                className="bg-white rounded-xl border border-gray-100 p-3.5 hover:shadow-md hover:border-gray-200 transition-all text-left group"
                data-testid={`kpi-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: kpi.bg }}>
                    <Icon size={15} style={{ color: kpi.color }} />
                  </div>
                </div>
                <p className="text-lg lg:text-xl font-bold text-gray-900">{kpi.value}</p>
                <p className="text-[10px] text-gray-500 font-medium leading-tight mt-0.5">{kpi.label}</p>
              </button>
            );
          })}
        </div>

        {/* ─── 3. FOUR ACTION PANELS ───────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="action-panels">
          {actionPanels.map((panel, i) => {
            const Icon = panel.icon;
            const hasWork = panel.count > 0;
            return (
              <button key={i} onClick={() => navigate(panel.path)}
                className={`rounded-xl border p-4 text-left transition-all group ${
                  hasWork ? 'bg-white border-gray-200 hover:shadow-md' : 'bg-gray-50/50 border-gray-100'
                }`}
                data-testid={`action-panel-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: panel.bg }}>
                    <Icon size={16} style={{ color: panel.color }} />
                  </div>
                  {hasWork && (
                    <span className="min-w-[22px] h-[22px] px-1.5 flex items-center justify-center rounded-full text-[11px] font-bold text-white"
                      style={{ backgroundColor: panel.color }}>
                      {panel.count}
                    </span>
                  )}
                </div>
                <p className="text-sm font-bold text-gray-900 mb-0.5">{panel.title}</p>
                <p className="text-[11px] text-gray-400">{panel.desc}</p>
                {hasWork && (
                  <div className="flex items-center gap-1 mt-2 text-[11px] font-semibold group-hover:gap-2 transition-all" style={{ color: panel.color }}>
                    Voir <ChevronRight size={12} />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* ─── 4. CHARTS (traffic, adhésions, cotisations) ─────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Traffic */}
          <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="traffic-chart">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-bold text-gray-900">Trafic</p>
                <p className="text-[10px] text-gray-400">{analytics?.traffic?.year} vs {(analytics?.traffic?.year || 2026) - 1}</p>
              </div>
              <TrendingUp size={14} className="text-[#35AFE4]" />
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={trafficData}>
                <defs>
                  <linearGradient id="cTraffic" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#35AFE4" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#35AFE4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb', fontSize: '11px' }} />
                {analytics?.traffic?.year && (
                  <>
                    <Area type="monotone" dataKey={`${analytics.traffic.year - 1}`} stroke="#d1d5db" fill="none" strokeWidth={1.5} strokeDasharray="4 4" />
                    <Area type="monotone" dataKey={`${analytics.traffic.year}`} stroke="#35AFE4" fill="url(#cTraffic)" strokeWidth={2} />
                  </>
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Adhésions */}
          <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="adhesion-chart">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-bold text-gray-900">Adhésions</p>
                <p className="text-[10px] text-gray-400">{analytics?.adhesions?.year} vs {(analytics?.adhesions?.year || 2026) - 1}</p>
              </div>
              <UserPlus size={14} className="text-emerald-500" />
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={adhesionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb', fontSize: '11px' }} />
                {analytics?.adhesions?.year && (
                  <>
                    <Bar dataKey={`${analytics.adhesions.year - 1}`} fill="#d1d5db" radius={[3, 3, 0, 0]} barSize={10} />
                    <Bar dataKey={`${analytics.adhesions.year}`} fill="#1e3a5f" radius={[3, 3, 0, 0]} barSize={10} />
                  </>
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Cotisations */}
          <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="revenue-chart">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-bold text-gray-900">Cotisations</p>
                <p className="text-[10px] text-gray-400">{analytics?.revenue?.year} vs {(analytics?.revenue?.year || 2026) - 1}</p>
              </div>
              <DollarSign size={14} className="text-amber-500" />
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={v => `${v}€`} />
                <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb', fontSize: '11px' }} formatter={(v) => [`${v}€`, '']} />
                {analytics?.revenue?.year && (
                  <>
                    <Bar dataKey={`${analytics.revenue.year - 1}`} fill="#d1d5db" radius={[3, 3, 0, 0]} barSize={10} />
                    <Bar dataKey={`${analytics.revenue.year}`} fill="#f59e0b" radius={[3, 3, 0, 0]} barSize={10} />
                  </>
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardHome;
